import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Checkbox,
  Typography,
  Box,
  Button,
  Chip,
  Link,
  IconButton,
  Menu,
  TextField,
  FormControlLabel,
  Divider,
  Skeleton,
  Tooltip,
  Radio,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import {
  FilterAlt as FilterIcon,
  FilterAltOff as FilterOffIcon,
} from "@mui/icons-material";
import {
  getAuditRuleCategory,
  getAuditRuleRefNo,
  getCoderName,
  getDrg,
  getEpisodeIdentifiers,
  getFileNames,
  getHealthFund,
  getMRNoptions,
  getPredictedDRG1,
  getPredictedDRG2,
  getPrincipalDiagnosis,
  getPrincipalProcedureCodes,
  getSpecialitiesFilter,
  getStatus,
  REQUEST_STATUS,
} from "../EnhanceFilters/EnhanceFiltersSlice";
import { useDispatch, useSelector } from "react-redux";
import {
  COLUMN_FILTER_PAYLOAD_MAP,
  formatDate,
  SORT_KEY_MAP,
  STATIC_FILTER_OPTIONS,
} from "./ColumnFilter";
import { setPayload } from "../pages/EnhanceWorkflow/EnhanceWorkflowSlice";
import { setCombinedPayload } from "../pages/combinedReview/CombinedReviewSlice";
import { updatedEnhancePlusPayload } from "../pages/EnhancePlusWorkflow/EnhancePlusWorkflowSlice";
const GROUP_BORDER = "1px solid #E2E8F0";

const GenericTable = ({
  columns = [],
  rows = [],
  selectable = true,
  rowsPerPage = 10,
  onRowClick,
  serverMode = false,
  loading = false,
  onSortChange,
  onFilterChange,
  onPageChange,
  onPageSizeChange,
  onSelectionChange,
  selectedRows = [],
  currentPage = 0,
  totalPages = 1,
  height = 620,
  activeModule,
  onSelectAllChange,
  filterResetKey,
}) => {
  const [localPage, setLocalPage] = useState(0);
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("asc");

  const [filters, setFilters] = useState({});
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [filterField, setFilterField] = useState(null);
  const [filterSearch, setFilterSearch] = useState("");
  const [isSelectAll, setIsSelectAll] = useState(false);
  const RADIO_FILTER_FIELDS = ["drgVersion"];
  const hasGroups = columns.some((c) => c.group);
  const page = serverMode ? currentPage : localPage;
  const paperRef = useRef(null);
  const headerRef = useRef(null);
  const footerRef = useRef(null);
  const firstRowRef = useRef(null);
  const fileNameToHeaderIdsRef = useRef({});
  const measuredRowHeightRef = useRef(41); // fallback until a real row renders
  const debounceTimerRef = useRef(null);
  const initialEmittedRef = useRef(false);
  const enhancePayload = useSelector((state) => state.codeEnhance.payload);
  const combinedPayload = useSelector((state) => state.combinedReview.payload);
  const enhancePlusPayload = useSelector(
    (state) => state.enhancePlusWorkflow.payload,
  );
  const [displayRows, setDisplayRows] = useState(rows);
  const COLUMN_FILTER_APIS = {
    episodeId: {
      thunk: getEpisodeIdentifiers,
      key: "episode",
    },
    fileName: {
      thunk: getFileNames,
      key: "file",
    },
    diagnosis: {
      thunk: getPrincipalDiagnosis,
      key: "principalDiagnosis",
    },
    currentDrg: {
      thunk: getDrg,
      key: "drg",
    },
    actualDrg: {
      thunk: getDrg,
      key: "drg",
    },
    predictedDrg1: {
      thunk: getPredictedDRG1,
      key: "predictedDrg1",
    },
    predictedDrg2: {
      thunk: getPredictedDRG2,
      key: "predictedDrg2",
    },
    healthFund: {
      thunk: getHealthFund,
      key: "healthfund",
    },
    auditRuleCategory: {
      thunk: getAuditRuleCategory,
      key: "auditRuleCategory",
    },
    auditStatus: {
      thunk: getStatus,
      key: "status",
    },
    auditRuleRefNo: {
      thunk: getAuditRuleRefNo,
      key: "auditRuleReferenceNo",
    },
    auditRuleReferenceNo: {
      thunk: getAuditRuleRefNo,
      key: "auditRuleReferenceNo",
    },
    speciality: {
      thunk: getSpecialitiesFilter,
      key: "specialities",
    },
    coderName: {
      thunk: getCoderName,
      key: "codername",
    },
    principalDiagnosis: {
      thunk: getPrincipalDiagnosis,
      key: "principalDiagnosis",
    },
    principalProcedureCode: {
      thunk: getPrincipalProcedureCodes,
      key: "principalProcedureCodes",
    },
    procedureDiagnosis: {
      thunk: getPrincipalProcedureCodes,
      key: "principalProcedureCodes",
    },
    mrn: {
      thunk: getMRNoptions,
      key: "mrn",
    },
  };
  useEffect(() => {
    if (!loading) {
      setDisplayRows(rows);
    }
  }, [rows, loading]);
  const dispatch = useDispatch();

  const dropdowns = useSelector((state) => state.enhanceFilters.dropdowns);

  // const handleSort = (field) => {
  //   const nextOrder = orderBy === field && order === "asc" ? "desc" : "asc";
  //   setOrderBy(field);
  //   setOrder(nextOrder);
  //   onSortChange?.({ field, order: nextOrder });
  // };
  const handleSort = (field) => {
    const nextOrder = orderBy === field && order === "asc" ? "desc" : "asc";

    setOrderBy(field);
    setOrder(nextOrder);

    onSortChange?.({
      field: SORT_KEY_MAP[field] || field,
      order: nextOrder,
    });
  };

const handleApplyFilter = () => {
  const payloadKey = COLUMN_FILTER_PAYLOAD_MAP[activeModule]?.[filterField];

  const currentPayload =
    activeModule === "Combined Review"
      ? combinedPayload
      : activeModule === "Enhance Plus Workflow"
        ? enhancePlusPayload
        : enhancePayload;

  let selectedValues = Array.from(activeFilterValues);
  const isRadioField = RADIO_FILTER_FIELDS.includes(filterField);

  let updatedPayload;

  if (isRadioField) {
    const singleValue = selectedValues.length > 0 ? String(selectedValues[0]) : "";
    updatedPayload = { ...currentPayload, [payloadKey]: singleValue, page_number: 1 };
  } else {
    const priorityFields = ["priority", "priorityValue", "codeEnhancePriority", "pallasPriority"];
    if (priorityFields.includes(filterField)) {
      selectedValues = selectedValues.map((value) =>
        typeof value === "string" && value.startsWith("P")
          ? parseInt(value.replace("P", ""), 10)
          : value
      );
    }

    updatedPayload = {
      ...currentPayload,
      [payloadKey]: selectedValues,
      page_number: 1,
    };

    // NEW: fileName needs a merged, ownership-aware header_id + file_type
    if (filterField === "fileName") {
      const modalOwnedIds = new Set();
      const modalOwnedNames = new Set(Object.keys(fileNameToHeaderIdsRef.current));
      Object.values(fileNameToHeaderIdsRef.current).forEach((set) =>
        set.forEach((id) => modalOwnedIds.add(id)),
      );

      // preserve ids/names this column filter's own dropdown never produced
      // (i.e. set by the toolbar's "Select Files" menu)
      const preservedHeaderIds = (currentPayload.header_id || []).filter(
        (id) => !modalOwnedIds.has(id),
      );
      const preservedFileTypes = (currentPayload.file_type || []).filter(
        (name) => !modalOwnedNames.has(name),
      );

      const selectedHeaderIds = Array.from(
        new Set(
          selectedValues.flatMap(
            (name) => Array.from(fileNameToHeaderIdsRef.current[name] || []),
          ),
        ),
      );

      updatedPayload.header_id = Array.from(new Set([...preservedHeaderIds, ...selectedHeaderIds]));
      updatedPayload[payloadKey] = Array.from(new Set([...preservedFileTypes, ...selectedValues]));
    }
  }

  if (activeModule === "Combined Review") {
    dispatch(setCombinedPayload(updatedPayload));
  } else if (activeModule === "Enhance Plus Workflow") {
    dispatch(updatedEnhancePlusPayload(updatedPayload));
  } else {
    dispatch(setPayload(updatedPayload));
  }

  closeFilter();
};
  const openFilter = (e, field) => {
    console.log("is_flagged at filter-open:", enhancePayload.is_flagged);
    setFilterAnchor(e.currentTarget);
    setFilterField(field);
    setFilterSearch("");

    // NEW — resync this field's selection from the currently-applied payload,
    // so the checkboxes always reflect what's truly applied (not stale dropdown pages)
    const currentPayload =
      activeModule === "Combined Review"
        ? combinedPayload
        : activeModule === "Enhance Plus Workflow"
          ? enhancePlusPayload
          : enhancePayload;

    const payloadKey = COLUMN_FILTER_PAYLOAD_MAP[activeModule]?.[field];
    if (payloadKey) {
      const appliedValue = currentPayload?.[payloadKey];
      const priorityFields = [
        "priority",
        "priorityValue",
        "codeEnhancePriority",
        "pallasPriority",
      ];

      if (
        appliedValue !== undefined &&
        appliedValue !== null &&
        appliedValue !== ""
      ) {
        let normalized = Array.isArray(appliedValue)
          ? appliedValue
          : [appliedValue];

        // reverse the P1/P2 -> 1/2 transform used when applying
        if (priorityFields.includes(field)) {
          normalized = normalized.map((v) =>
            typeof v === "number" || /^\d+$/.test(String(v)) ? `P${v}` : v,
          );
        }

        setFilters((prev) => ({
          ...prev,
          [field]: new Set(normalized.map(String)),
        }));
      } else {
        // nothing applied for this field — make sure stale local state doesn't linger
        setFilters((prev) => {
          const next = { ...prev };
          delete next[field];
          return next;
        });
      }
    }

    // const staticOptions = STATIC_FILTER_OPTIONS[activeModule]?.[field];
    // if (staticOptions) {
    //   return;
    // }
    // If values are already selected, don't fetch immediately.
    // Only selected values will be shown until the user searches.
    if (filters[field]?.size > 0) {
      return;
    }

    const staticOptions = STATIC_FILTER_OPTIONS[activeModule]?.[field];
    if (staticOptions) return;

    const config = COLUMN_FILTER_APIS[field];
    if (!config) return;

    dispatch(
      config.thunk({
        search: "",
        page_number: 1,
        page_size: 10,
      }),
    );
  };
  const currentDropdown =
    filterField && COLUMN_FILTER_APIS[filterField]
      ? dropdowns[COLUMN_FILTER_APIS[filterField].key]
      : null;
  const dropdownLoading = STATIC_FILTER_OPTIONS[activeModule]?.[filterField]
    ? false
    : currentDropdown?.status === REQUEST_STATUS.LOADING;

  const hasLoadedOptions = (currentDropdown?.data?.length ?? 0) > 0;

  const dropdownLoadingMore = STATIC_FILTER_OPTIONS[activeModule]?.[filterField]
    ? false
    : dropdownLoading && hasLoadedOptions;

  const hasMore = STATIC_FILTER_OPTIONS[activeModule]?.[filterField]
    ? false
    : currentDropdown?.hasMore;
  const closeFilter = () => {
    setFilterAnchor(null);
    setFilterField(null);
  };
  const handleFilterSearch = (_, value) => {
    setFilterSearch(value);

    // Don't call API until user types something
    if (value.trim() === "") return;

    const staticOptions = STATIC_FILTER_OPTIONS[activeModule]?.[filterField];
    if (staticOptions) return;

    const config = COLUMN_FILTER_APIS[filterField];
    if (!config) return;

    dispatch(
      config.thunk({
        search: value,
        page_number: 1,
        page_size: 10,
      }),
    );
  };
  const handleLoadMore = () => {
    const config = COLUMN_FILTER_APIS[filterField];

    if (!config) return;

    if (dropdownLoading) return;

    if (!currentDropdown?.hasMore) return;

    const nextPage = (currentDropdown.page || 1) + 1;

    dispatch(
      config.thunk({
        search: filterSearch,
        page_number: nextPage,
        page_size: 10,
      }),
    );
  };

  const uniqueValuesForField = (field) =>
    Array.from(new Set(rows.map((r) => String(r[field] ?? "")))).sort();

  const toggleFilterValue = (field, value) => {
    const isRadioField = RADIO_FILTER_FIELDS.includes(field);

    if (isRadioField) {
      // For radio buttons, set only the selected value
      setFilters((prev) => {
        const next = { ...prev };
        // If the same value is clicked again, clear it (deselect)
        const currentValues = prev[field] || new Set();
        if (currentValues.has(value)) {
          next[field] = new Set(); // Clear selection
          onFilterChange?.({ field, values: [] });
        } else {
          next[field] = new Set([value]);
          onFilterChange?.({ field, values: [value] });
        }
        return next;
      });
    } else {
      // For checkboxes, toggle the value
      setFilters((prev) => {
        const current = new Set(prev[field] || []);
        current.has(value) ? current.delete(value) : current.add(value);
        const next = { ...prev, [field]: current };
        onFilterChange?.({ field, values: Array.from(current) });
        return next;
      });
    }
  };

  const clearFieldFilter = (field) => {
    setFilters((prev) => {
      const next = { ...prev };
      delete next[field];
      onFilterChange?.({ field, values: [] });
      return next;
    });
  };

  const processedRows = useMemo(() => {
    if (serverMode) return rows;
    let result = rows;
    Object.entries(filters).forEach(([field, valuesSet]) => {
      if (valuesSet && valuesSet.size > 0) {
        result = result.filter((r) => valuesSet.has(String(r[field] ?? "")));
      }
    });
    if (orderBy) {
      result = [...result].sort((a, b) => {
        const av = a[orderBy],
          bv = b[orderBy];
        if (av < bv) return order === "asc" ? -1 : 1;
        if (av > bv) return order === "asc" ? 1 : -1;
        return 0;
      });
    }
    return result;
  }, [rows, filters, orderBy, order, serverMode]);

  const pageCount = serverMode
    ? totalPages
    : Math.max(1, Math.ceil(processedRows.length / rowsPerPage));

  const pagedRows = serverMode
    ? displayRows
    : processedRows?.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      );

  const goToPage = (p) => {
    if (!serverMode) {
      setLocalPage(p);
    }
    onPageChange?.(p);
  };

  const groupRow = useMemo(() => {
    const groups = [];
    columns.forEach((col) => {
      const last = groups[groups.length - 1];
      if (last && last.label === col.group) last.span += 1;
      else groups.push({ label: col.group || "", span: 1 });
    });
    return groups;
  }, [columns]);

  const groupBoundaryFields = useMemo(() => {
    const boundaries = new Set();
    if (!hasGroups) return boundaries;
    for (let i = 0; i < columns.length; i++) {
      const cur = columns[i];
      const next = columns[i + 1];
      if (!next || next.group !== cur.group) {
        if (i !== columns.length - 1) boundaries.add(cur.field);
      }
    }
    return boundaries;
  }, [columns, hasGroups]);

  // const isRowSelected = (row) =>
  //   selectedRows.some((item) => item.episodeId === row.episodeId);
  const isRowSelected = (row) =>
    selectedRows.some((item) => item.hcpLineId === row.hcpLineId);

  // "Select all" only affects the CURRENT PAGE — selections on other pages are preserved.
  const allPagedRowsSelected =
    pagedRows.length > 0 && pagedRows.every((r) => isRowSelected(r));
  const somePagedRowsSelected =
    pagedRows.some((r) => isRowSelected(r)) && !allPagedRowsSelected;

  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    onSelectAllChange?.(checked);
    let updatedRows;
    if (checked) {
      const toAdd = pagedRows.filter((r) => !isRowSelected(r));
      updatedRows = [...selectedRows, ...toAdd];
    } else {
      updatedRows = selectedRows.filter(
        (item) => !pagedRows.some((r) => r.hcpLineId === item.hcpLineId),
      );
    }
    onSelectionChange?.(updatedRows);
  };

  const handleRowSelect = (row) => {
    let updatedRows;

    if (isRowSelected(row)) {
      updatedRows = selectedRows.filter(
        (item) => item.hcpLineId !== row.hcpLineId,
      );
    } else {
      updatedRows = [...selectedRows, row];
    }

    onSelectionChange?.(updatedRows);
  };
  useEffect(() => {
    if (selectedRows.length === 0) {
      setIsSelectAll(false);
    }
  }, [selectedRows]);
  useEffect(() => {
    if (filterResetKey === undefined) return; // no-op if parent doesn't use this
    setFilters({});
    setFilterAnchor(null);
    setFilterField(null);
    setFilterSearch("");
    setOrderBy("");
    setOrder("asc");
  }, [filterResetKey]);
  useEffect(() => {
    if (!serverMode || !onPageSizeChange) return;

    const computeAndEmit = () => {
      const container = paperRef.current;
      if (!container) return;

      const containerHeight = container.clientHeight;
      const headerHeight =
        headerRef.current?.offsetHeight || (hasGroups ? 76 : 40);
      const footerHeight = footerRef.current?.offsetHeight || 49;

      if (firstRowRef.current) {
        measuredRowHeightRef.current =
          firstRowRef.current.offsetHeight || measuredRowHeightRef.current;
      }

      const available = containerHeight - headerHeight - footerHeight;
      const fittingRows = Math.max(
        5,
        Math.floor(available / measuredRowHeightRef.current),
      );
      if (!initialEmittedRef.current) {
        initialEmittedRef.current = true;
        onPageSizeChange(fittingRows);
        return;
      }

      if (fittingRows > 0 && fittingRows !== rowsPerPage) {
        onPageSizeChange(fittingRows);
      }
    };

    const ro = new ResizeObserver(() => {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(computeAndEmit, 150);
    });

    if (paperRef.current) ro.observe(paperRef.current);
    computeAndEmit();

    return () => {
      ro.disconnect();
      clearTimeout(debounceTimerRef.current);
    };
  }, [serverMode, onPageSizeChange, rowsPerPage, hasGroups, rows.length]);
  

useEffect(() => {
  const fileDropdownData = dropdowns?.file?.data;
  if (fileDropdownData?.length) {
    fileDropdownData.forEach((f) => {
      if (!fileNameToHeaderIdsRef.current[f.file_name]) {
        fileNameToHeaderIdsRef.current[f.file_name] = new Set();
      }
      fileNameToHeaderIdsRef.current[f.file_name].add(f.header_id);
    });
  }
}, [dropdowns?.file?.data]);

  const renderCellValue = (col, row) => {
    if (col.renderCell) return col.renderCell(row);

    // Helper function to format as Australian dollars
    const formatAUD = (value) => {
      if (value === undefined || value === null || value === "") return null;
      const numValue =
        typeof value === "string"
          ? parseFloat(value.replace(/[,$]/g, ""))
          : value;
      if (isNaN(numValue) || numValue === 0) return null;
      return numValue.toLocaleString("en-AU", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    };

    // Status column - Add this block
    if (col.field === "status" || col.field === "auditStatus") {
      const value = row[col.field];

      if (!value || (typeof value === "string" && value.trim() === "")) {
        return "-------";
      }

      const statusStr = String(value).toLowerCase();
      let color = "#6B7280";
      let bgColor = "#F3F4F6";

      if (statusStr === "passed" || statusStr === "pass") {
        color = "#16A34A";
        bgColor = "#ECFDF5";
      } else if (statusStr === "failed" || statusStr === "fail") {
        color = "#D32F2F";
        bgColor = "#FDECEC";
      } else if (statusStr === "pending") {
        color = "#D97706";
        bgColor = "#FEF3C7";
      }

      return (
        <Chip
          label={value}
          size="small"
          sx={{
            bgcolor: bgColor,
            color: color,
            fontWeight: 600,
            borderRadius: "6px",
            height: 28,
            fontSize: "0.8rem",
          }}
        />
      );
    }
    // Handle date columns based on type property
    if (col.type === "date") {
      const value = row[col.field];
      if (!value) return "-------";
      const formattedDate = formatDate(value);
      return formattedDate || "-------";
    }
    // Priority fields
    if (col.field === "priority" || col.field === "codeEnhancePriority") {
      const priorities = row[col.field];

      if (
        !priorities ||
        (Array.isArray(priorities) && priorities.length === 0) ||
        (typeof priorities === "string" && priorities.trim() === "")
      ) {
        return "-------";
      }

      let values = [];
      if (Array.isArray(priorities)) {
        values = priorities.filter((p) => p != null && p !== "");
      } else if (priorities) {
        values = [priorities];
      }

      if (values.length === 0) {
        return "-------";
      }

      return (
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "nowrap" }}>
          {values.map((priority) => {
            const style =
              Number(priority) === 1
                ? { bgcolor: "#FDECEC", color: "#D32F2F" }
                : Number(priority) === 2
                  ? { bgcolor: "#E8F1FD", color: "#2563EB" }
                  : Number(priority) === 3
                    ? { bgcolor: "#FEF3C7", color: "#D97706" }
                    : Number(priority) === 4
                      ? { bgcolor: "#EDE9FE", color: "#7C3AED" }
                      : Number(priority) === 5
                        ? { bgcolor: "#ECFDF5", color: "#16A34A" }
                        : { bgcolor: "#F3F4F6", color: "#6B7280" };
            return (
              <Chip
                key={priority}
                label={`P${priority}`}
                size="small"
                sx={{
                  ...style,
                  fontWeight: 700,
                  borderRadius: "6px",
                  minWidth: 38,
                  height: 28,
                  fontSize: "0.8rem",
                  flexShrink: 0,
                }}
              />
            );
          })}
        </Box>
      );
    }

    // Priority Value / Pallas Priority
    if (col.field === "priorityValue" || col.field === "pallasPriority") {
      const value = row[col.field];

      // Check for null, undefined, empty string, or whitespace-only string
      if (!value || (typeof value === "string" && value.trim() === "")) {
        return "-------";
      }

      // If value is an array (just in case), handle it
      if (Array.isArray(value) && value.length === 0) {
        return "-------";
      }

      // If value is an array with items, use the first one
      const displayValue = Array.isArray(value) ? value[0] : value;
      const label =
        typeof displayValue === "object"
          ? displayValue.username ||
            displayValue.name ||
            displayValue.label ||
            displayValue.user_id
          : displayValue;

      const style = /ultra/i.test(displayValue)
        ? { bgcolor: "#FDECEC", color: "#D32F2F" }
        : /high/i.test(displayValue)
          ? { bgcolor: "#FEF3C7", color: "#D97706" }
          : /medium/i.test(displayValue)
            ? { bgcolor: "#EDE9FE", color: "#7C3AED" }
            : /low/i.test(displayValue)
              ? { bgcolor: "#E8F1FD", color: "#2563EB" }
              : { bgcolor: "#F3F4F6", color: "#6B7280" };

      return (
        <Chip
          label={label}
          size="small"
          sx={{ ...style, fontWeight: 600, borderRadius: "6px", height: 28 }}
        />
      );
    }

    // Delta fields - Red for negative, Green for positive
    if (col.field === "delta1" || col.field === "delta2") {
      const value = row[col.field];
      if (value === undefined || value === null || value === "") {
        return "-------";
      }

      const numValue =
        typeof value === "string"
          ? parseFloat(value.replace(/[,$]/g, ""))
          : value;

      if (isNaN(numValue)) {
        return "-------";
      }

      // Format with Australian dollar style with comma separators
      const formattedValue = Math.abs(numValue).toLocaleString("en-AU", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      const formatted =
        numValue >= 0 ? `+$${formattedValue}` : `-$${formattedValue}`;

      const color = numValue >= 0 ? "#22C55E" : "#EF4444";

      return (
        <Typography
          component="span"
          sx={{
            color: color,
            fontWeight: 600,
            fontSize: "0.82rem",
          }}
        >
          {formatted}
        </Typography>
      );
    }

    // Reimbursement fields with Australian dollar formatting
    if (
      col.field === "actualReimbursement" ||
      col.field === "predictedReimbursement1" ||
      col.field === "predictedReimbursement2" ||
      col.field === "currentDrgRevenue" ||
      col.field === "auditDrgRevenue"
    ) {
      const value = row[col.field];
      if (value === undefined || value === null || value === "") {
        return "-------";
      }

      const numValue =
        typeof value === "string"
          ? parseFloat(value.replace(/[,$]/g, ""))
          : value;

      if (isNaN(numValue) || numValue === 0) {
        return "-------";
      }

      return `$${numValue.toLocaleString("en-AU", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }

    // Episode ID with link
    if (col.field === "episodeId") {
      return (
        <Link
          component="button"
          underline="always"
          onClick={() => onRowClick?.(row)}
          sx={{
            fontSize: "0.82rem",
            fontWeight: 500,
            color: "#1565C0",
            textDecorationColor: "#1565C0",
          }}
        >
          {row.episodeId}
        </Link>
      );
    }
    const rawValue = row[col.field];

    if (
      rawValue !== null &&
      typeof rawValue === "object" &&
      !React.isValidElement(rawValue)
    ) {
      if (Array.isArray(rawValue)) {
        return rawValue
          .map((item) =>
            item && typeof item === "object"
              ? item.username || item.name || item.label || item.user_id
              : item,
          )
          .join(", ");
      }
     return (
  rawValue.username ||
  rawValue.name ||
  rawValue.label ||
  rawValue.file_name ||
  rawValue.user_id ||
  ""
);
    }

    return rawValue;
  };

  const filterMenuOpen = Boolean(filterAnchor);
  const activeFilterValues = filterField
    ? filters[filterField] || new Set()
    : new Set();
    // Add near the top of GenericTable, alongside other helpers
const getOptionValue = (item) => {
  if (item === null || typeof item !== "object") return item;
  return (
    item.username ??
    item.file_name ??
    item.name ??
    item.label ??
    item.value ??
    ""
  );
};
const filterOptions = useMemo(() => {
  const staticOptions = STATIC_FILTER_OPTIONS[activeModule]?.[filterField];
  let options = staticOptions ? (staticOptions.options || staticOptions) : (currentDropdown?.data || []);

  const selectedValues = Array.from(activeFilterValues).map(String);

  const uniqueOptions = Array.from(
    new Map(options.map((item) => [String(getOptionValue(item)), item])).values()
  );

  selectedValues.forEach((selectedValue) => {
    const exists = uniqueOptions.some((item) => String(getOptionValue(item)) === selectedValue);
    if (!exists) uniqueOptions.unshift(selectedValue);
  });

  const filteredOptions = uniqueOptions.filter((item) =>
    String(getOptionValue(item)).toLowerCase().includes(filterSearch.toLowerCase())
  );

  return filteredOptions.sort((a, b) => {
    const selectedA = activeFilterValues.has(String(getOptionValue(a)));
    const selectedB = activeFilterValues.has(String(getOptionValue(b)));
    if (selectedA && !selectedB) return -1;
    if (!selectedA && selectedB) return 1;
    return 0;
  });
}, [filterField, activeModule, filterSearch, currentDropdown, activeFilterValues]);

  const getCellTitle = (col, row) => {
    const value = row[col.field];
    if (value === undefined || value === null || value === "") return "";
    if (Array.isArray(value)) return value.join(", ");
    return String(value);
  };
  const EllipsisCell = ({ title, children }) => {
    const textRef = React.useRef(null);
    const [isOverflowing, setIsOverflowing] = useState(false);

    useEffect(() => {
      const el = textRef.current;
      if (el) {
        setIsOverflowing(el.scrollWidth > el.clientWidth);
      }
    }, [children]);

    return (
      <Tooltip
        title={title}
        arrow
        placement="top"
        disableHoverListener={!isOverflowing || !title}
      >
        <Box
          ref={textRef}
          component="span"
          sx={{
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {children}
        </Box>
      </Tooltip>
    );
  };
  return (
    <Paper
      elevation={0}
      ref={paperRef}
      sx={{
        border: "none",
        borderRadius: 0,
        display: "flex",
        flexDirection: "column",
        height,
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      {/* Only THIS scrolls. Empty space (few rows) shows here, inside the body area. */}
      <Box
        sx={{
          position: "relative",
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto",WebkitOverflowScrolling: "touch" }}>
          <Table stickyHeader size="small" sx={{ minWidth: { xs: 640, sm: 720, md: "100%" } }}>
            <TableHead>
              {hasGroups && (
                <TableRow>
                  {selectable && (
                    <TableCell
                      padding="checkbox"
                      sx={{
                        bgcolor: "#F1F5F9",
                        borderBottom: "1px solid #e0e0e0",
                      }}
                    />
                  )}
                  {groupRow.map((g, i) => (
                    <TableCell
                      key={i}
                      colSpan={g.span}
                      sx={{
                        bgcolor: "#F1F5F9",
                        color: "#8A94A6",
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        letterSpacing: "0.5px",
                        borderBottom: "1px solid #e0e0e0",
                        borderRight:
                          i !== groupRow.length - 1 ? GROUP_BORDER : "none",
                        py: 0.75,
                      }}
                    >
                      {g.label}
                    </TableCell>
                  ))}
                </TableRow>
              )}

              <TableRow>
                {selectable && (
                  <TableCell padding="checkbox" sx={{ bgcolor: "#fff" }}>
                    <Checkbox
                      size="small"
                      indeterminate={somePagedRowsSelected}
                      checked={allPagedRowsSelected}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                )}

                {columns.map((col) => {
                  const isSortable = col.sortable !== false;
                  const isFilterable = col.filterable !== false;
                  const hasActiveFilter =
                    filters[col.field] && filters[col.field].size > 0;
                  return (
                    <TableCell
                      key={col.field}
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.75rem",
                        backgroundColor: "#fff",
                        color: "#334155",
                        borderRight: groupBoundaryFields.has(col.field)
                          ? GROUP_BORDER
                          : "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.25,
                        }}
                      >
                        {isSortable ? (
                          <TableSortLabel
                            active={orderBy === col.field}
                            direction={orderBy === col.field ? order : "asc"}
                            onClick={() => handleSort(col.field)}
                            sx={{
                              "& .MuiTableSortLabel-icon": {
                                opacity: "1 !important",
                              },
                            }}
                          >
                            {col.headerName}
                          </TableSortLabel>
                        ) : (
                          col.headerName
                        )}
                        {isFilterable && (
                          <IconButton
                            size="small"
                            sx={{ p: 0.25, ml: 0.25 }}
                            onClick={(e) => openFilter(e, col.field)}
                          >
                            <FilterIcon
                              sx={{
                                fontSize: 15,
                                color: hasActiveFilter ? "#1976d2" : "#9AA4B2",
                              }}
                            />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>

            <TableBody>
              {pagedRows.length === 0 && loading ? (
                Array.from({ length: rowsPerPage }).map((_, idx) => (
                  <TableRow key={`skeleton-${idx}`}>
                    {selectable && (
                      <TableCell padding="checkbox">
                        <Skeleton
                          variant="rectangular"
                          width={18}
                          height={18}
                        />
                      </TableCell>
                    )}
                    {columns.map((col) => (
                      <TableCell key={col.field}>
                        <Skeleton variant="text" width="80%" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : pagedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} sx={{ py: 6 }}>
                    <Typography variant="body1" color="text.secondary">
                      No Records Found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                pagedRows.map((row, idx) => {
                  const selected = isRowSelected(row);
                  return (
                    <TableRow
                      key={row.hcpLineId ?? row.episodeId ?? idx}
                      ref={idx === 0 ? firstRowRef : undefined}
                      hover
                      selected={selected}
                    >
                      {selectable && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={selected}
                            onChange={() => handleRowSelect(row)}
                          />
                        </TableCell>
                      )}
                      {columns.map((col) => {
                        const isPriorityCol =
                          col.field === "priority" ||
                          col.field === "codeEnhancePriority";
                        return (
                          <TableCell
                            key={col.field}
                            sx={{
                              fontSize: "0.82rem",
                              borderRight: groupBoundaryFields.has(col.field)
                                ? GROUP_BORDER
                                : "none",
                              maxWidth: isPriorityCol
                                ? "none"
                                : col.maxWidth || 200,
                              whiteSpace: isPriorityCol ? "nowrap" : "nowrap",
                              overflow: isPriorityCol ? "visible" : "hidden",
                              textOverflow: isPriorityCol ? "clip" : "ellipsis",
                            }}
                          >
                            {isPriorityCol ? (
                              renderCellValue(col, row)
                            ) : (
                              <EllipsisCell title={getCellTitle(col, row)}>
                                {renderCellValue(col, row)}
                              </EllipsisCell>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {loading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              bgcolor: "rgba(255,255,255,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10,
            }}
          >
            <Paper
              elevation={3}
              sx={{ px: 4, py: 3, borderRadius: 2, textAlign: "center" }}
            >
              <CircularProgress size={32} sx={{ mb: 1.5 }} />
              <Typography sx={{ fontWeight: 600, textAlign: "center" }}>
                Loading episodes...
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: "center" }}
              >
                Fetching latest data from server
              </Typography>
            </Paper>
          </Box>
        )}
      </Box>
      {/* Filter dropdown: search + checkbox list */}
      <Menu
        anchorEl={filterAnchor}
        open={filterMenuOpen}
        onClose={closeFilter}
        // PaperProps={{ sx: { width: 200, maxHeight: 340 } }}
         PaperProps={{ sx: { width: { xs: "88vw", sm: 200 }, maxWidth: 260, maxHeight: 340 }}}
      >
        <Box sx={{ p: 1.25 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search values"
            value={filterSearch}
            onChange={(e) => {
              setFilterSearch(e.target.value);
              // For static options, we don't need to make API calls
              const staticOptions =
                STATIC_FILTER_OPTIONS[activeModule]?.[filterField];
              if (!staticOptions) {
                handleFilterSearch(null, e.target.value);
              }
            }}
            sx={{ mb: 1 }}
          />

          <Box
            sx={{
              maxHeight: 200,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
            }}
            onScroll={(e) => {
              const node = e.currentTarget;
              const reachedBottom =
                node.scrollHeight - node.scrollTop - node.clientHeight < 5;
              if (reachedBottom) {
                handleLoadMore();
              }
            }}
          >
            {dropdownLoading &&
            !STATIC_FILTER_OPTIONS[activeModule]?.[filterField] &&
            !hasLoadedOptions ? (
              <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <>
                {filterOptions.length === 0 ? (
                  <Typography
                    sx={{
                      py: 2,
                      textAlign: "center",
                      color: "text.secondary",
                      fontSize: "0.875rem",
                    }}
                  >
                    No options available
                  </Typography>
                ) : (
                  filterOptions.map((value) => {
                      const optionValue = getOptionValue(value);
  const optionLabel = getOptionValue(value);
                    const isRadioField =
                      RADIO_FILTER_FIELDS.includes(filterField);
                    return (
                      <FormControlLabel
                        key={optionValue}
                        sx={{
                          width: "100%",
                          m: 0,
                          py: 0.25,
                          alignItems: "center",
                        }}
                        control={
                          isRadioField ? (
                            <Radio
                              size="small"
                              checked={activeFilterValues.has(optionValue)}
                              onChange={() =>
                                toggleFilterValue(filterField, optionValue)
                              }
                            />
                          ) : (
                            <Checkbox
                              size="small"
                              checked={activeFilterValues.has(optionValue)}
                              onChange={() =>
                                toggleFilterValue(filterField, optionValue)
                              }
                            />
                          )
                        }
                        label={
                          <Typography
                            sx={{
                              fontSize: "13px",
                              fontWeight: 400,
                            }}
                          >
                            {optionLabel}
                          </Typography>
                        }
                      />
                    );
                  })
                )}

                {dropdownLoadingMore && (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 1 }}
                  >
                    <CircularProgress size={18} />
                  </Box>
                )}

                {!dropdownLoading &&
                  !STATIC_FILTER_OPTIONS[activeModule]?.[filterField] &&
                  !currentDropdown?.hasMore &&
                  filterOptions.length > 0 && (
                    <Typography
                      sx={{
                        py: 1,
                        textAlign: "center",
                        color: "text.secondary",
                      }}
                    >
                      No more data
                    </Typography>
                  )}
              </>
            )}
          </Box>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Button
              size="small"
              startIcon={<FilterOffIcon sx={{ fontSize: 16 }} />}
              onClick={() => clearFieldFilter(filterField)}
              sx={{ textTransform: "none" }}
            >
              Clear
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleApplyFilter}
              sx={{ textTransform: "none", boxShadow: "none" }}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Menu>

      {/* Pinned footer — always sits at the bottom edge of this fixed-height Paper */}
      <Box
        ref={footerRef}
        sx={{
          p: 1,
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 2,
          borderTop: "1px solid #e0e0e0",
          bgcolor: "#fafbfc",
          flexShrink: 0,
        }}
      >
        <Button
          size="small"
          disabled={page === 0}
          onClick={() => goToPage(Math.max(0, page - 1))}
          sx={{ textTransform: "none" }}
        >
          ← Prev
        </Button>
        <Typography variant="body2" color="text.secondary">
          Page {page + 1} of {pageCount}
        </Typography>
        <Button
          size="small"
          disabled={page + 1 >= pageCount}
          onClick={() => goToPage(Math.min(pageCount - 1, page + 1))}
          sx={{ textTransform: "none" }}
        >
          Next →
        </Button>
      </Box>
    </Paper>
  );
};

export default GenericTable;
